import React, { useState } from 'react';
import api from '../../services/api';
import { notify } from '../../utils/notifications';
import { Check, Undo2, Euro, Gift, AlertTriangle, Tag } from 'lucide-react';
import ModalIncassoRata from './ModalIncassoRata';
import ModalSconto from './ModalSconto';

/**
 * Griglia delle mensilità di un abbonamento scolastico.
 *
 * Sostituisce l'inserimento manuale dei pagamenti: le rate esistono già,
 * all'operatore resta un solo clic per segnarle incassate.
 */
const RateAbbonamento = ({ abbonamento, onAggiornato }) => {
  const [rataDaIncassare, setRataDaIncassare] = useState(null);
  const [scontoAperto, setScontoAperto] = useState(false);
  const [inCorso, setInCorso] = useState(null);

  const rate = abbonamento?.rate || [];

  if (!rate.length) {
    return null;
  }

  const totaleDovuto = rate.reduce((acc, r) => acc + Number(r.importo), 0);
  const totalePagato = rate.filter((r) => r.pagata).reduce((acc, r) => acc + Number(r.importo), 0);
  const residuo = totaleDovuto - totalePagato;
  const scadute = rate.filter((r) => r.scaduta);

  const annulla = async (rata) => {
    if (!window.confirm(`Annullare l'incasso di ${rata.etichetta_periodo}?`)) return;
    setInCorso(rata.id);
    const toastId = notify.loading('Annullamento...');
    try {
      await api.post(`/abbonamenti/rate/${rata.id}/annulla_pagamento/`);
      notify.update(toastId, { type: 'success', render: 'Incasso annullato' });
      onAggiornato?.();
    } catch (error) {
      notify.update(toastId, {
        type: 'error',
        render: error.response?.data?.errore || 'Errore nell\'annullamento',
      });
    } finally {
      setInCorso(null);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-5 py-3 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-bold text-gray-900">
            Mensilità {abbonamento.anno_scolastico_display}
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Settembre a metà quota, giugno gratuito
            {abbonamento.sconto_descrizione && ` · ${abbonamento.sconto_descrizione}`}
          </p>
        </div>

        <button
          onClick={() => setScontoAperto(true)}
          className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-lg hover:border-gray-900 transition"
        >
          <Tag className="w-3.5 h-3.5" />
          {abbonamento.sconto_descrizione ? 'Modifica sconto' : 'Applica scontistica'}
        </button>
      </div>

      <div className="grid grid-cols-3 divide-x divide-gray-200 border-b border-gray-200 bg-gray-50">
        <div className="px-4 py-3">
          <p className="text-xs text-gray-500">Totale anno</p>
          <p className="text-base font-bold text-gray-900">
            €{totaleDovuto.toFixed(2)}
            {abbonamento.sconto_descrizione
              && Number(abbonamento.totale_senza_sconto) > totaleDovuto && (
              <span className="ml-2 text-xs font-normal text-gray-400 line-through">
                €{Number(abbonamento.totale_senza_sconto).toFixed(2)}
              </span>
            )}
          </p>
        </div>
        <div className="px-4 py-3">
          <p className="text-xs text-gray-500">Incassato</p>
          <p className="text-base font-bold text-green-700">€{totalePagato.toFixed(2)}</p>
        </div>
        <div className="px-4 py-3">
          <p className="text-xs text-gray-500">Residuo</p>
          <p className={`text-base font-bold ${residuo > 0 ? 'text-gray-900' : 'text-green-700'}`}>
            €{residuo.toFixed(2)}
          </p>
        </div>
      </div>

      {scadute.length > 0 && (
        <div className="px-5 py-2.5 bg-amber-50 border-b border-amber-200 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
          <p className="text-xs text-amber-800">
            {scadute.length === 1
              ? '1 mensilità non saldata il cui periodo è già passato'
              : `${scadute.length} mensilità non saldate il cui periodo è già passato`}
          </p>
        </div>
      )}

      <div className="divide-y divide-gray-100">
        {rate.map((rata) => (
          <div
            key={rata.id}
            className={`px-5 py-3 flex items-center justify-between gap-3 ${
              rata.scaduta ? 'bg-amber-50/40' : ''
            }`}
          >
            <div className="flex items-center gap-3 min-w-0">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                  rata.gratuita
                    ? 'bg-blue-100 text-blue-700'
                    : rata.pagata
                    ? 'bg-green-100 text-green-700'
                    : rata.scaduta
                    ? 'bg-amber-100 text-amber-700'
                    : 'bg-gray-100 text-gray-500'
                }`}
              >
                {rata.gratuita ? <Gift className="w-4 h-4" /> : rata.pagata ? <Check className="w-4 h-4" /> : <Euro className="w-4 h-4" />}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900 capitalize truncate">
                  {rata.etichetta_periodo}
                </p>
                <p className="text-xs text-gray-500">
                  {rata.gratuita
                    ? 'Inclusa senza costo'
                    : rata.pagata
                    ? `Incassata il ${new Date(rata.data_pagamento).toLocaleDateString('it-IT')}${rata.metodo ? ` · ${rata.metodo.toLowerCase()}` : ''}`
                    : 'Da incassare'}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 shrink-0">
              <span className={`text-sm font-bold ${rata.gratuita ? 'text-blue-700' : 'text-gray-900'}`}>
                {rata.gratuita ? 'Gratis' : `€${Number(rata.importo).toFixed(2)}`}
              </span>

              {!rata.gratuita && (
                rata.pagata ? (
                  <button
                    onClick={() => annulla(rata)}
                    disabled={inCorso === rata.id}
                    title="Annulla incasso"
                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition disabled:opacity-40"
                  >
                    <Undo2 className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    onClick={() => setRataDaIncassare(rata)}
                    disabled={inCorso === rata.id}
                    className="px-3 py-1.5 text-xs font-medium bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition disabled:opacity-40"
                  >
                    Incassa
                  </button>
                )
              )}
            </div>
          </div>
        ))}
      </div>

      <ModalIncassoRata
        rata={rataDaIncassare}
        onClose={() => setRataDaIncassare(null)}
        onConfermato={onAggiornato}
      />

      {scontoAperto && (
        <ModalSconto
          abbonamento={abbonamento}
          onClose={() => setScontoAperto(false)}
          onApplicato={onAggiornato}
        />
      )}
    </div>
  );
};

export default RateAbbonamento;
